import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Importing icons to match the image
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import ArchiveIcon from '@material-ui/icons/Archive';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';

export const DEMAND_SUMMARY_METRICS = [
    { label: 'Latest Demand', value: '12.4M cases', delta: '+7% vs operating plan', tone: 'blue', icon: <TrendingUpIcon />, deltaTone: 'good' },
    { label: 'Forecast Accuracy', value: '87.4%', delta: '+4 pp vs baseline', tone: 'green', icon: <TrackChangesIcon />, deltaTone: 'good' },
    { label: 'Upside Identified', value: '0.9M cases', delta: 'priority demand pockets', tone: 'purple', icon: <ArchiveIcon />, deltaTone: 'blue' },
    { label: 'Demand at Risk', value: '0.6M cases', delta: 'supply / availability gap', tone: 'orange', icon: <AttachMoneyIcon />, deltaTone: 'bad' },
];

export const DEMAND_WEEKLY_SIGNAL = [
    { week: 'W39', plan: 480, latest: 500 },
    { week: 'W40', plan: 540, latest: 610 },
    { week: 'W41', plan: 590, latest: 700 },
    { week: 'W42', plan: 620, latest: 705 },
    { week: 'W43', plan: 640, latest: 680 },
    { week: 'W44', plan: 660, latest: 690 },
];

export const DEMAND_SIGNAL_DRIVERS = [
    { label: 'Primary sales', detail: 'Run-rate vs plan', tag: 'State × Brand × SKU', tone: 'good' },
    { label: 'Inventory', detail: 'Days cover / stock-out risk', tag: 'Depot / market', tone: 'blue' },
    { label: 'Seasonality', detail: 'Festival / event / weather pattern', tag: 'Week × State', tone: 'warn' },
    { label: 'Commercial', detail: 'Price / promotion / channel mix', tag: 'Brand × Channel', tone: 'warn' },
    { label: 'Supply feedback', detail: 'Availability / constrained SKU', tag: 'Site × Market', tone: 'bad' },
];

export const DEMAND_EXCEPTIONS = [
    { state: 'Maharashtra', brand: 'Premium A', plan: '620k', latest: '710k', variance: '+14.5%', varianceTone: 'good', driver: 'Higher run-rate / premium mix', change: 'Increase production + redeploy stock', value: '₹18 Cr' },
    { state: 'Karnataka', brand: 'Core B', plan: '410k', latest: '455k', variance: '+11.0%', varianceTone: 'good', driver: 'GT demand acceleration', change: 'Increase deployment / protect fill', value: '₹9 Cr' },
    { state: 'Delhi NCR', brand: 'Premium F', plan: '355k', latest: '390k', variance: '+9.9%', varianceTone: 'good', driver: 'Premium demand upside', change: 'Prioritize constrained supply', value: '₹6 Cr' },
    { state: 'West Bengal', brand: 'Core D', plan: '300k', latest: '255k', variance: '−15.0%', varianceTone: 'bad', driver: 'Demand below operating plan', change: 'Reduce production / avoid excess', value: '₹5 Cr' },
    { state: 'Telangana', brand: 'Value E', plan: '285k', latest: '335k', variance: '+17.5%', varianceTone: 'good', driver: 'Emerging availability gap', change: 'Reallocate inventory immediately', value: '₹7 Cr' },
];

// Sampled directly from the reference screenshot so the render is pixel-matched.
const CHART_COLORS = {
    plan: '#b4c3dc',
    latest: '#ef6617',
};

export class Demand extends React.Component {
    getChartOptions() {
        const categories = DEMAND_WEEKLY_SIGNAL.map((w) => w.week);
        const planData = DEMAND_WEEKLY_SIGNAL.map((w) => w.plan);
        const latestData = DEMAND_WEEKLY_SIGNAL.map((w) => w.latest);

        return {
            chart: {
                type: 'column',
                height: 300,
                backgroundColor: 'transparent',
                style: { fontFamily: 'inherit' },
            },
            title: { text: undefined },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: '#eef0f6',
                tickColor: '#eef0f6',
                labels: { style: { color: '#6b7690', fontSize: '13px' } },
            },
            yAxis: {
                title: { text: undefined },
                gridLineColor: '#f1f3f8',
                labels: { style: { color: '#6b7690', fontSize: '12px' } },
            },
            legend: {
                align: 'center',
                verticalAlign: 'top',
                symbolRadius: 2,
                itemStyle: { color: '#16305c', fontWeight: '600', fontSize: '13px' },
            },
            tooltip: {
                shared: true,
                valueSuffix: 'k cases',
            },
            plotOptions: {
                column: {
                    borderRadius: 4,
                    borderWidth: 0,
                    groupPadding: 0.15,
                    pointPadding: 0.08,
                },
            },
            series: [
                {
                    name: 'Operating Plan',
                    data: planData,
                    color: CHART_COLORS.plan,
                },
                {
                    name: 'Latest Demand',
                    data: latestData,
                    color: CHART_COLORS.latest,
                },
            ],
        };
    }

    render() {
        return (
            <div className="cc-page">
                <div className="cc-header">
                    <div className="cc-header-text">
                        <h1>Demand Intelligence — Plan vs Latest Demand</h1>
                        <p>Sense demand shifts by State × Channel × Brand × SKU and translate them into production, bottling and deployment changes.</p>
                    </div>
                    <div className="cc-header-filters">
                        <button className="cc-filter-btn">Next 8 Weeks</button>
                        <button className="cc-filter-btn">All States</button>
                        <button className="cc-filter-btn">All Brands</button>
                    </div>
                </div>

                {/* Updated Metric Cards to match image layout */}
                <div className="cc-metric-row">
                    {DEMAND_SUMMARY_METRICS.map((metric) => (
                        <div className={`cc-metric-card tone-${metric.tone}`} key={metric.label}>
                            <div className="cc-metric-icon-wrap">{metric.icon}</div>
                            <div className="cc-metric-content">
                                <div className="cc-metric-label">{metric.label}</div>
                                <div className="cc-metric-value">{metric.value}</div>
                                <div className={`cc-metric-delta cc-delta-${metric.deltaTone}`}>
                                    {metric.delta}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="cc-main-row">
                    <div className="cc-panel cc-chart-panel">
                        <h2>Operating Plan vs Latest Demand Signal</h2>
                        <p className="cc-panel-sub">Weekly demand is continuously refreshed using available primary sales, inventory and operating signals.</p>

                        <HighchartsReact highcharts={Highcharts} options={this.getChartOptions()} />

                        <p className="cc-callout">
                            Signal &#8594; quantify variance &#8594; identify driver &#8594; determine production / deployment response
                        </p>
                    </div>

                    <div className="cc-panel cc-signals-panel">
                        <h2>Demand Signals &amp; Drivers</h2>
                        <div className="cc-signal-list">
                            {DEMAND_SIGNAL_DRIVERS.map((signal) => (
                                <div className="cc-signal-row dmd-signal-row" key={signal.label}>
                                    <div className="dmd-signal-text-wrap">
                                        <span className="dmd-signal-label">{signal.label}</span>
                                        <span className="dmd-signal-detail">{signal.detail}</span>
                                    </div>
                                    <span className={`dmd-signal-tag dmd-tone-${signal.tone}`}>{signal.tag}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="cc-panel cc-actions-panel">
                    <h2>Demand Exceptions &#8594; Required Operating Change</h2>
                    <p className="cc-panel-sub">Prioritize only material deviations — and connect each demand signal to a supply-chain decision.</p>

                    <table className="cc-actions-table">
                        <thead>
                            <tr>
                                <th>State / Market</th>
                                <th>Brand / SKU</th>
                                <th>Plan</th>
                                <th>Latest Demand</th>
                                <th>Variance</th>
                                <th>Likely Driver</th>
                                <th>Required Change</th>
                                <th>Value at Stake</th>
                            </tr>
                        </thead>
                        <tbody>
                            {DEMAND_EXCEPTIONS.map((row) => (
                                <tr key={`${row.state}-${row.brand}`}>
                                    <td>{row.state}</td>
                                    <td>{row.brand}</td>
                                    <td>{row.plan}</td>
                                    <td>{row.latest}</td>
                                    <td className={`cc-delta-${row.varianceTone}`}>{row.variance}</td>
                                    <td>{row.driver}</td>
                                    <td>{row.change}</td>
                                    <td className="cc-delta-good">{row.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <p className="cc-footnote dmd-footnote">
                        Illustrative UX and values. The design intentionally works with primary and available operating data; secondary sales can enrich the signal when available.
                    </p>
                </div>
            </div>
        );
    }
}

export default Demand;